import React, { useState, useEffect } from 'react';
import { submitFormData } from '../../../../pages/api';

const Cleaning = ({ formData, onFormDataChange }) => {
    const { cleaningObservations = [], nonConformance = '' } = formData;

    useEffect(() => {
        // Destructure formData to get initial values
        const { cleaningObservations = [], nonConformance = '' } = formData;
    
        // Check if formData already has prefilled data
        const hasFormData =
            cleaningObservations.length > 0 || nonConformance.trim() !== '';
    
        if (!hasFormData) {
            // Step 1: Fetch metadata from sessionStorage
            const metadata = JSON.parse(sessionStorage.getItem('metadata'));
            let prefilledCleaningData = [];
            let prefilledObservations = [];
            let prefilledNonConformance = '';
    
            // Step 2: Check metadata for prefilled data
            if (
                metadata &&
                metadata.post &&
                metadata.post.Cleaning_post
            ) {
                prefilledCleaningData = metadata.post.Cleaning_post;
                prefilledObservations = prefilledCleaningData.map((item) => ({
                    product: item.product,
                    specifications: item.specifications,
                    frequency: item.frequency,
                    observation1: '',
                    observation2: '',
                }));
            }
    
            // Step 3: Fetch inspectionData from sessionStorage
            const inspectionData = JSON.parse(
                sessionStorage.getItem('inspectionData')
            );
    
            // Step 4: Check inspectionData for observations and non-conformance data
            if (
                inspectionData &&
                inspectionData.PostLamination &&
                inspectionData.PostLamination.Cleaning_post
            ) {
                const { cleaning, nonConformance: inspectionNonConformance } =
                    inspectionData.PostLamination.Cleaning_post;
    
                // Override prefilled data with inspectionData if available
                prefilledCleaningData = cleaning.map((item) => ({
                    product: item.product,
                    specifications: item.specifications,
                    frequency: item.frequency,
                    observation1: item.observation1 || '',
                    observation2: item.observation2 || '',
                }));
    
                prefilledObservations = prefilledCleaningData.map((item) => ({
                    product: item.product,
                    specifications: item.specifications,
                    frequency: item.frequency,
                    observation1: item.observation1 || '',
                    observation2: item.observation2 || '',
                }));
    
                prefilledNonConformance = inspectionNonConformance || '';
            }
    
            // Step 5: Update formData only if prefilled data exists
            if (
                prefilledCleaningData.length > 0 ||
                prefilledObservations.length > 0 ||
                prefilledNonConformance.trim() !== ''
            ) {
                onFormDataChange({
                    ...formData,
                    cleaningObservations: prefilledObservations,
                    nonConformance: prefilledNonConformance,
                });
            }
        }
    }, [formData, onFormDataChange]);

    const handleObservationChange = (index, field, value) => {
        const updatedObservations = [...cleaningObservations];
        updatedObservations[index] = {
            ...updatedObservations[index],
            [field]: value,
        };
        onFormDataChange({ ...formData, cleaningObservations: updatedObservations });
    };

    const handleNonConformanceChange = (e) => {
        onFormDataChange({ ...formData, nonConformance: e.target.value });
    };

    const submitObservations = async () => {
        const incompleteObservation = cleaningObservations.some(
            (obs) => !obs.observation1.trim()
        );
        if (incompleteObservation) {
            alert('Please fill all Observation 1 fields before submitting.');
            return;
        }
        try {
            const projectId = localStorage.getItem('selectedProject');
            const inspectionDate = sessionStorage.getItem('inspectionDate');
            const inspectionShift = sessionStorage.getItem('inspectionShift');
            const inspectorName = sessionStorage.getItem('username');
            const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');

            const combinedData = cleaningObservations.map((item, index) => ({
                product: item.product,
                specifications: item.specifications,
                frequency: item.frequency,
                observation1: cleaningObservations[index]?.observation1 || '',
                observation2: cleaningObservations[index]?.observation2 || '',
            }));

            const payload = {
                projectId,
                inspectionSupplier,
                inspectionDate,
                inspectionShift,
                inspectorName,
                status: 'completed',
                PostLamination: {
                    Cleaning_post: {
                        cleaning: combinedData,
                        nonConformance: nonConformance,
                    },
                },
            };

            await submitFormData(payload);
            alert('Observations submitted successfully!');
        } catch (error) {
            console.error('Error submitting observations:', error);
            alert('Failed to submit observations.');
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">
                Cleaning Inspection
            </h3>
            {/* Cleaning Appearance Table */}
            <h4 className="text-lg font-semibold mt-6 mb-4 text-gray-700">
                Cleaning Appearance
            </h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px] rounded-tr-lg">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cleaningObservations?.map((item, index) => (
                            <tr
                                key={index}
                                className={`border-b ${
                                    index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                                } hover:bg-gray-100`}
                            >
                                <td className="py-3 px-4">{item.product}</td>
                                <td className="py-3 px-4">{item.specifications}</td>
                                <td className="py-3 px-4">{item.frequency}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={item.observation1 || ''}
                                        onChange={(e) =>
                                            handleObservationChange(
                                                index,
                                                'observation1',
                                                e.target.value
                                            )
                                        }
                                        placeholder="Enter observation 1"
                                        className="w-full border p-2 rounded-lg focus:outline-none"
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={item.observation2 || ''}
                                        onChange={(e) =>
                                            handleObservationChange(
                                                index,
                                                'observation2',
                                                e.target.value
                                            )
                                        }
                                        placeholder="Enter observation 2"
                                        className="w-full border p-2 rounded-lg focus:outline-none"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {/* Non Conformance Input */}
            <div className="mt-6">
                <h4 className="text-lg font-semibold mb-2 text-gray-700">
                    Non Conformance
                </h4>
                <textarea
                    value={nonConformance || ''}
                    onChange={handleNonConformanceChange}
                    placeholder="Enter details about non conformance..."
                    className="w-full border p-4 rounded-lg focus:outline-none"
                    rows={4}
                />
            </div>
            {/* Submit Observations */}
            <button
                onClick={submitObservations}
                className="mt-4 px-6 py-2 bg-[#424752] text-white rounded-lg hover:bg-gray-500"
            >
                Submit
            </button>
        </div>
    );
};

export default Cleaning;